import React, { useState, useEffect, useRef, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import { Formik, Field, FastField } from 'formik';
import axios from 'axios';
import { getHeaders } from '../../../request';
import { useMoment } from '../../../hooks';
import validationSchema from './validationSchema';
import { temPerfilRBAC } from '../../../secutity/acl';
import PropTypes from 'prop-types';
import { PERFIL } from '../../../common/Constants';
import { SttGrid, SttDatePicker, SttInput, SttAutocomplete, SttTranslateHook, SttMaskedInput } from '@stt-componentes/core';
import { SttClearButton, SttSearchButton } from "@stt-componentes/core-v2";
import { DATA_DE, DATA_ATE, NOME_SOLICITANTE, NOME_PACIENTE, CPF_PACIENTE, CNS_PACIENTE, UF, MUNICIPIO } from './fieldNames';

const useStyles = makeStyles((theme) => ({
	buttonWrapper: {
		display: 'flex',
		alignItems: 'center'
	}
}));

const estadoInicialForm = {
	de: null,
	ate: null,
	solicitante: '',
	paciente: '',
	cpf: '',
	cns: '',
	uf: null,
	municipio: null
};

const Form = (props) => {
	const { callbackBusca, callbackAndamento, page, count, orderBy, order, buscar, resetPageOrder } = props;

	const { strings } = useContext(SttTranslateHook.I18nContext);
	const user = useSelector((state) => state.index.user);
	const schema = validationSchema(strings);
	const moment = useMoment();
	const [carregarUfs, setCarregarUfs] = useState(false);
	const [ufs, setUfs] = useState([]);
	const [municipios, setMunicipios] = useState([]);
	const formRef = useRef();
	const classes = useStyles();
	const UTILITARIOS_API_BASE_URL = global.gConfig.url_base_utilitarios;

	useEffect(() => {
		if (temPerfilRBAC(user, PERFIL.ADMINISTRADOR) || temPerfilRBAC(user, PERFIL.ADMINISTRADOR_ESTADUAL)) {
			setCarregarUfs(true);
		}
	}, []);

	useEffect(() => {
		if (carregarUfs) {
			let url = `${UTILITARIOS_API_BASE_URL}/localizacao/estado`;
			if (temPerfilRBAC(user, PERFIL.ADMINISTRADOR_ESTADUAL)) {
				const perfil = user.perfisRBAC.filter((p) => p.identificador === PERFIL.ADMINISTRADOR_ESTADUAL);
				const estados = perfil[0].estados;
				const idsUf = estados.map((e) => e.id);
				url = `${url}?ids=${idsUf.join(',')}`;
			}
			axios
				.get(url)
				.then((response) => {
					if (response.data) {
						const { itens } = response.data.data;
						setUfs(itens);
					}
				})
				.catch((err) => console.log(err));
		}
	}, [carregarUfs]);

	useEffect(() => {
		if (buscar && formRef.current) {
			formRef.current.handleSubmit();
		}
	}, [buscar]);

	const handleChangeUf = (uf) => {
		if (uf) {
			axios
				.get(`${UTILITARIOS_API_BASE_URL}/localizacao/cidade?id-estado=${uf.id}`)
				.then((response) => {
					if (response.data) {
						const { itens } = response.data.data;
						setMunicipios(itens);
					} else {
						setMunicipios([]);
					}
				})
				.catch((err) => console.log(err));
		} else {
			setMunicipios([]);
		}
	};

	return (
		<Formik
			innerRef={formRef}
			initialValues={estadoInicialForm}
			validationSchema={schema}
			onSubmit={(data, { setSubmitting }) => {
				setSubmitting(true);
				callbackAndamento(true);

				let params = {
					start: page * count,
					count: count
				};
				if (data.de) {
					params.de = moment(data.de).format('YYYY-MM-DD');
				}
				if (data.ate) {
					params.ate = moment(data.ate).format('YYYY-MM-DD');
				}
				if (data.solicitante) {
					params.solicitante = data.solicitante.trim();
				}
				if (data.paciente) {
					params.paciente = data.paciente.trim();
				}
				if (data.cpf) {
					params.cpf = data.cpf;
				}
				if (data.cns) {
					params.cns = data.cns;
				}
				if (data.uf) {
					params.uf = data.uf.id;
				}
				if (data.municipio) {
					params.municipio = data.municipio.id;
				}
				if (orderBy && order) {
					params.sort = orderBy;
					params.direction = order;
				}

				axios
					.get(`${global.gConfig.url_base_eeg}/solicitacao`, { params, headers: getHeaders() })
					.then((response) => {
						const dados = response.data.data;
						callbackBusca(dados);
					})
					.catch((err) => {
						console.error(err);
						callbackBusca(null);
					})
					.finally(() => {
						setSubmitting(false);
						callbackAndamento(false);
					});
			}}
		>
			{({ isSubmitting, values, handleSubmit, resetForm, submitForm }) => {
				return (
					<form
						onSubmit={handleSubmit}
						noValidate
					>
						<SttGrid
							container
							spacing={3}
						>
							<SttGrid
								item
								xs={12}
								sm={12}
								md={6}
								lg={5}
								xl={4}
							>
								<SttGrid
									container
									spacing={3}
								>
									<SttGrid
										item
										xs={12}
										sm={6}
									>
										<FastField name={DATA_DE}>
											{({ field: { name, value }, form: { setFieldValue, setFieldError, setFieldTouched }, meta }) => {
												return (
													<SttDatePicker
														label={strings.dataInicialSolicitacao}
														inputprops={{
															name: name
														}}
														disableFuture
														maxDate={new Date()}
														error={meta.touched && meta.error ? true : false}
														onError={(error) => {
															setFieldError(DATA_DE, meta.error || error);
														}}
														value={value}
														onBlur={() => {
															setFieldTouched(DATA_DE, true);
														}}
														helperText={meta.touched && meta.error ? meta.error : undefined}
														onChange={(date) => setFieldValue(DATA_DE, date, true)}
														onClose={() => setFieldTouched(DATA_DE, true)}
													/>
												);
											}}
										</FastField>
									</SttGrid>
									<SttGrid
										item
										xs={12}
										sm={6}
									>
										<FastField name={DATA_ATE}>
											{({ field: { name, value }, form: { setFieldValue, setFieldError, setFieldTouched }, meta }) => {
												return (
													<SttDatePicker
														label={strings.dataFinalSolicitacao}
														inputprops={{
															name: name
														}}
														disableFuture
														maxDate={new Date()}
														error={meta.touched && meta.error ? true : false}
														onError={(error) => {
															setFieldError(DATA_ATE, meta.error || error);
														}}
														value={value}
														onBlur={() => {
															setFieldTouched(DATA_ATE, true);
														}}
														helperText={meta.touched && meta.error ? meta.error : undefined}
														onChange={(date) => setFieldValue(DATA_ATE, date, true)}
														onClose={() => setFieldTouched(DATA_ATE, true)}
													/>
												);
											}}
										</FastField>
									</SttGrid>
								</SttGrid>
							</SttGrid>
							<SttGrid
								item
								xs={12}
								sm={6}
								md={3}
								lg={2}
								xl={2}
							>
								<FastField name={NOME_SOLICITANTE}>
									{({ field }) => (
										<SttInput
											{...field}
											label={strings.solicitante}
										/>
									)}
								</FastField>
							</SttGrid>
							<SttGrid
								item
								xs={12}
								sm={6}
								md={3}
								lg={2}
								xl={2}
							>
								<FastField name={NOME_PACIENTE}>
									{({ field }) => (
										<SttInput
											{...field}
											label={strings.paciente}
										/>
									)}
								</FastField>
							</SttGrid>
							<SttGrid
								item
								xs={12}
								sm={6}
								md={3}
								lg={3}
								xl={2}
							>
								<FastField name={CPF_PACIENTE}>
									{({ field, meta }) => (
										<SttMaskedInput
											{...field}
											mask="cpf"
											label={strings.cpfPaciente}
											error={meta.touched && meta.error ? true : false}
											helperText={meta.touched && meta.error ? meta.error : undefined}
										/>
									)}
								</FastField>
							</SttGrid>
							<SttGrid
								item
								xs={12}
								sm={6}
								md={3}
								lg={3}
								xl={2}
							>
								<FastField name={CNS_PACIENTE}>
									{({ field, meta }) => (
										<SttMaskedInput
											{...field}
											mask="cns"
											label={strings.cnsPaciente}
											error={meta.touched && meta.error ? true : false}
											helperText={meta.touched && meta.error ? meta.error : undefined}
										/>
									)}
								</FastField>
							</SttGrid>
							{carregarUfs && (
								<>
									<SttGrid
										item
										xs={12}
										sm={3}
										md={2}
										lg={1}
										xl={1}
									>
										<Field name={UF}>
											{({ field: { name, value, onBlur }, form: { setFieldValue } }) => (
												<SttAutocomplete
													inputprops={{
														name: name,
														label: strings.uf
													}}
													getOptionLabel={(option) => option?.sigla || ''}
													getOptionSelected={(option, val) => option?.id === val?.id}
													options={ufs}
													value={value}
													onBlur={onBlur}
													onChange={(e, item) => {
														const uf = item || null;
														setFieldValue(UF, uf);
														setFieldValue(MUNICIPIO, null);
														handleChangeUf(uf);
													}}
												/>
											)}
										</Field>
									</SttGrid>
									<SttGrid
										item
										xs={12}
										sm={9}
										md={4}
										lg={2}
										xl={3}
									>
										<Field name={MUNICIPIO}>
											{({ field: { name, value, onBlur }, form: { setFieldValue } }) => (
												<SttAutocomplete
													inputprops={{
														name: name,
														label: strings.municipio
													}}
													getOptionLabel={(option) => option?.nome || ''}
													getOptionSelected={(option, v) => option?.id === v?.id}
													options={municipios}
													value={value}
													onBlur={onBlur}
													onChange={(e, item) => {
														setFieldValue(MUNICIPIO, item || null);
													}}
												/>
											)}
										</Field>
									</SttGrid>
								</>
							)}
						</SttGrid>
						<SttGrid
							container
							spacing={3}
						>
							<SttGrid
								item
								xs={12}
								className={classes.buttonWrapper}
							>
								<SttSearchButton
									type="submit"
									variant="contained"
									color="primary"
									disabled={isSubmitting}
									nomarginleft="true"
									onClick={() => {
										resetPageOrder();
										submitForm();
									}}
								>
									{strings.pesquisar}
								</SttSearchButton>
								<SttClearButton
									type="button"
									variant="outlined"
									color="primary"
									disabled={isSubmitting}
									onClick={() => {
										resetPageOrder();
										resetForm({
											values: estadoInicialForm
										});
										callbackBusca(null);
									}}
								>
									{strings.limpar}
								</SttClearButton>
							</SttGrid>
						</SttGrid>
					</form>
				);
			}}
		</Formik>
	);
};

Form.propTypes = {
	callbackBusca: PropTypes.func.isRequired,
	callbackAndamento: PropTypes.func.isRequired,
	page: PropTypes.number.isRequired,
	count: PropTypes.number.isRequired,
	orderBy: PropTypes.string,
	order: PropTypes.string.isRequired,
	buscar: PropTypes.bool.isRequired,
	resetPageOrder: PropTypes.func.isRequired
};

export default Form;
